import { Routes, Router } from '@angular/router';
import { LoginComponent } from './auth/login/login';
import { ClaimComponent } from './auth/claim/claim';
import { FrozenAccountComponent } from './auth/frozen-account/frozen-account';
import { MainLayoutComponent } from './shared/layout/main-layout/main-layout';
import { PluginLayoutComponent } from './shared/layout/plugin-layout/plugin-layout';
import { UserListComponent } from './users/user-list/user-list';
import { UserManagementComponent } from './user-management/user-management';
import { AuthGuard } from './core/guards/auth.guard';
import { LoginGuard } from './core/guards/login.guard';
import { unsavedChangesGuard } from './core/guards/unsaved-changes.guard';
import { PluginLoginComponent } from './plugin/login/plugin-login.component';
import { PluginDashboardComponent } from './plugin/dashboard/plugin-dashboard.component';
import { ReleasesComponent } from './features/releases/releases.component';
import { PluginsLayoutComponent } from './features/plugins-layout/plugins-layout.component';
import { InvitesComponent } from './team/invites/invites.component';
import { inject } from '@angular/core';
import { AuthService } from './core/services/auth.service';

export const routes: Routes = [
  {
    path: 'login',
    component: LoginComponent,
    canActivate: [LoginGuard]
  },
  {
    path: 'claim',
    component: ClaimComponent
  },
  {
    path: 'frozen-account',
    component: FrozenAccountComponent
  },
  // Plugin routes
  {
    path: 'plugin/login',
    component: PluginLoginComponent  // no layout
  },
  {
    path: 'plugin/dashboard',
    component: PluginLayoutComponent,
    canActivate: [AuthGuard],
    children: [
      {
        path: '',
        component: PluginDashboardComponent
      }
    ]
  },
  {
    path: 'change-password',
    loadComponent: () => import('./auth/change-password/change-password').then(m => m.ChangePasswordComponent),
    canActivate: [AuthGuard]
  },
  {
    path: '',
    component: MainLayoutComponent,
    canActivate: [AuthGuard],
    children: [
      {
        path: 'team-management',
        component: UserManagementComponent,
        children: [
          {
            path: 'employees',
            component: UserListComponent
          },
          {
            path: 'invites',
            redirectTo: 'employees',
            pathMatch: 'full'
          },
          {
            path: '',
            redirectTo: 'employees',
            pathMatch: 'full'
          }
        ]
      },
      {
        path: 'download',
        loadComponent: () => import('./features/download/download.component').then(m => m.DownloadComponent)
      },
      {
        path: 'plugins',
        component: PluginsLayoutComponent,
        canActivate: [() => {
          const auth = inject(AuthService);
          const router = inject(Router);
          const user = auth.getCurrentUser();
          return user ? true : router.createUrlTree(['/login']);
        }],
        children: [
          {
            path: 'releases',
            component: ReleasesComponent
          },
          {
            path: 'permissions',
            loadComponent: () => import('./features/permissions/permissions.component').then(m => m.PermissionsComponent),
            canActivate: [() => {
              const auth = inject(AuthService);
              const router = inject(Router);
              const role = auth.getCurrentUser()?.systemRole ?? 0;
              return (role === 1 || role === 2) ? true : router.createUrlTree(['/plugins/releases']);
            }]
          },
          {
            path: 'telemetry',
            loadComponent: () => import('./features/telemetry/telemetry.component').then(m => m.TelemetryComponent),
            canActivate: [() => {
              const auth = inject(AuthService);
              const router = inject(Router);
              const role = auth.getCurrentUser()?.systemRole ?? 0;
              return (role === 1 || role === 2 || role === 3) ? true : router.createUrlTree(['/plugins/releases']);
            }]
          },
          {
            path: 'qa-profiles',
            loadComponent: () => import('./features/qa-profiles/qa-profiles.component').then(m => m.QaProfilesComponent),
            canDeactivate: [unsavedChangesGuard],
            canActivate: [() => {
              const auth = inject(AuthService);
              const router = inject(Router);
              const role = auth.getCurrentUser()?.systemRole ?? 0;
              return (role === 1 || role === 2 || role === 3) ? true : router.createUrlTree(['/plugins/releases']);
            }]
          },
          {
            path: 'qa-dictionaries',
            loadComponent: () => import('./features/qa-dictionaries/qa-dictionaries.component').then(m => m.QaDictionariesComponent),
            canActivate: [() => {
              const auth = inject(AuthService);
              const router = inject(Router);
              const role = auth.getCurrentUser()?.systemRole ?? 0;
              return (role === 1 || role === 2 || role === 3) ? true : router.createUrlTree(['/plugins/releases']);
            }]
          },
          {
            path: 'qa-reports',
            loadComponent: () => import('./features/qa-reports/qa-reports.component').then(m => m.QaReportsComponent),
            canActivate: [() => {
              const auth = inject(AuthService);
              const router = inject(Router);
              const role = auth.getCurrentUser()?.systemRole ?? 0;
              return (role === 1 || role === 2 || role === 3) ? true : router.createUrlTree(['/plugins/releases']);
            }]
          },
          {
            path: 'feedback',
            loadComponent: () => import('./features/feedback/feedback.component').then(m => m.FeedbackComponent),
            canActivate: [() => {
              const auth = inject(AuthService);
              const router = inject(Router);
              const role = auth.getCurrentUser()?.systemRole ?? 0;
              return (role === 1 || role === 2 || role === 3) ? true : router.createUrlTree(['/plugins/releases']);
            }]
          },
          {
            path: '',
            redirectTo: 'releases',
            pathMatch: 'full'
          }
        ]
      },
      {
        path: '',
        redirectTo: '/team-management',
        pathMatch: 'full'
      }
    ]
  },
  {
    path: '**',
    redirectTo: '/login'
  }
];
